import { useState, useEffect } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import CSVReader from 'react-csv-reader'
import { v4 as uuidv4 } from 'uuid'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import clsx from 'clsx'

import { AdminListItem } from '../components'

import { sortStringFunc, delay } from '../utils'
import { ROLE_PARTICIPANT, TOAST_OFFERING_INSTANCE_INFO_ID } from '../constants'
import {
  INVITE_SUBJECT,
  GET_OFFERINGS,
  GET_INVITES,
  CREATE_OFFERING_MUTATION,
  CREATE_BULK_INVITE_MUTATION,
  SEND_INVITE_EMAIL_MUTATION,
  RESET_OFFERING_MUTATION,
  generateInviteBody,
} from '../services/admin'
import { apolloOfferingStatusData } from '../services/apollo'

const AdminNewOffering = () => {
  const [startDate, setStartDate] = useState(() => new Date())
  const [inviteList, setInvitelist] = useState([])
  const [createOfferingMutation] = useMutation(CREATE_OFFERING_MUTATION)
  const [createBulkInviteMutation] = useMutation(CREATE_BULK_INVITE_MUTATION)
  const [sendInviteEmailMutation] = useMutation(SEND_INVITE_EMAIL_MUTATION)
  const [resetOffering] = useMutation(RESET_OFFERING_MUTATION)
  const [createOfferingInProgress, setCreateOfferingInProgress] =
    useState(false)
  const [completedPercentage, setCompletedPercentage] = useState(0)
  const {
    loading: invitesDataLoading,
    error: invitesDataError,
    data: invitesData,
  } = useQuery(GET_INVITES, {
    fetchPolicy: 'no-cache',
  })
  const [
    getOffering,
    {
      loading: offeringsDataLoading,
      error: offeringsDataError,
      data: offeringsData,
    },
  ] = useLazyQuery(GET_OFFERINGS, {
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    getOffering()
  }, [getOffering])

  useEffect(() => {
    if (
      !offeringsData ||
      offeringsData.offerings.edges.length === 0 ||
      offeringsDataLoading
    ) {
      return
    }

    const offeringDate = offeringsData.offerings.edges[0].node.offeringDate

    if (offeringDate.includes('-')) {
      const offeringDateArr = offeringDate.split('-')

      const formattedOfferingDate = new Date(
        offeringDateArr[0],
        offeringDateArr[1] - 1,
        offeringDateArr[2]
      )
      setStartDate(formattedOfferingDate)
    } else {
      setStartDate(new Date())
    }
  }, [offeringsData, offeringsDataLoading])

  /**
   * Handle loading csv file
   *
   * @param {[*]} data - Data list
   */
  const handleFileLoaded = (data) => {
    // No data. Do nothing.
    if (data.length === 0) {
      return
    }

    // Check for column header
    const header = data[0][0]
    if (header.toUpperCase() !== 'EMAIL') {
      return
    }

    const list = data
      // Remove the column header, blank line, non-email, and save the list array
      .filter((d, i) => i > 0 && d[0].length > 0 && d[0].includes('@'))
      .map((d) => d[0])
      .map((d) => {
        return {
          email: d,
          role: ROLE_PARTICIPANT, // Default to participant
        }
      })
    setInvitelist(list)
  }

  /**
   * Handle upload invite list csv file
   */
  const handleFileUploadButton = () => {
    const fileUploadElement = document.getElementById('upload-list')
    fileUploadElement.click()
  }

  /**
   * Handle role selection
   */
  const handleRoleSelection = (email, role) => () => {
    // Locate the list item by email
    const itemIndex = inviteList.findIndex((l) => l.email === email)

    if (itemIndex === -1) {
      console.log(`Role selection :: item not found :: ${email}`)
      // toast.error(`Item not found: ${email}`)
      return
    }

    // Update the item's role
    let newInviteList = Array.from(inviteList)

    // Don't update if role hasn't changed
    if (newInviteList[itemIndex].role === role) {
      return
    }

    // Update the state
    newInviteList[itemIndex].role = role
    setInvitelist(newInviteList)
  }

  /**
   * Send email
   *
   * @param {string} to - Receiver email address
   * @returns
   */
  const _sendInviteEmail = async (to) => {
    return sendInviteEmailMutation({
      variables: {
        to: to,
        subject: INVITE_SUBJECT,
        body: generateInviteBody(),
      },
    })
  }

  /**
   * Handle new offering creation
   */
  const handleCreateNewOffering = async () => {
    let createOfferingData

    if (inviteList.length === 0) {
      return
    }

    // Dismiss any toast notification if any
    toast.dismiss(TOAST_OFFERING_INSTANCE_INFO_ID)

    try {
      const createOfferingTaskCount = 1
      const sendEmailTaskCount = 1
      const inviteListTaskCount = 1
      const receiverPerBatch = 49 // Max is 50. Reserve one for Bcc address.
      const sendingCount = Math.ceil(inviteList.length / receiverPerBatch) // Send an invite email
      const totalTaskCount =
        createOfferingTaskCount +
        inviteListTaskCount +
        sendingCount +
        sendEmailTaskCount
      let taskCompletedCount = 0
      let taskCompletedPercentage = 0

      setCreateOfferingInProgress(true)

      // Create offering post
      createOfferingData = await createOfferingMutation({
        variables: {
          clientMutationId: uuidv4(),
          offeringDate: format(startDate, 'yyyy-LL-dd'),
        },
      })
      taskCompletedCount++
      taskCompletedPercentage = Math.round(
        ((taskCompletedCount + 1) / totalTaskCount) * 100
      )
      setCompletedPercentage(taskCompletedPercentage)

      // Create invites in bulk
      const invites = inviteList.map((p) => {
        return { email: p.email, role: p.role }
      })
      await createBulkInviteMutation({
        variables: {
          clientMutationId: uuidv4(),
          invites: invites,
        },
      })

      taskCompletedCount++
      taskCompletedPercentage = Math.round(
        ((taskCompletedCount + 1) / totalTaskCount) * 100
      )
      setCompletedPercentage(taskCompletedPercentage)

      for (let j = 0; j < sendingCount; j++) {
        const sendingList = inviteList
          .map((l) => l.email)
          .slice(j * receiverPerBatch, (j + 1) * receiverPerBatch)
          .toString()

        // Slow down sending email until we have the new increase in AWS SES quota
        if (j > 0) {
          await delay(400)
        }

        await _sendInviteEmail(sendingList)
        taskCompletedCount++
        taskCompletedPercentage = Math.round(
          ((taskCompletedCount + 1) / totalTaskCount) * 100
        )
        setCompletedPercentage(taskCompletedPercentage)
      }

      // Signal offering create status
      apolloOfferingStatusData({ created: true })
    } catch (err) {
      console.log('Create offering error', JSON.stringify(err))

      // Revert the platform
      const offeringId =
        createOfferingData?.data?.createOffering?.offering?.databaseId ?? null
      if (offeringId) {
        try {
          await resetOffering({
            variables: {
              id: offeringId,
            },
          })
        } catch (err) {
          console.log(`Reset offering failed :: err`, JSON.stringify(err))
        }
      }

      // Show toast notification
      toast.error(
        `Unable to create a new offering. Please refresh the page and try again!`
      )
    } finally {
      setCreateOfferingInProgress(false)

      // Refresh the offering data
      getOffering()
    }
  }

  if (offeringsDataError || invitesDataError) {
    return <p>Error loading data!</p>
  }

  const offerings = offeringsData?.offerings?.edges ?? []
  const invites = invitesData?.invites?.edges ?? []

  return (
    <>
      <div>Start Date:</div>
      {invitesDataLoading ? (
        <div className="mb-10 ml-6 mt-2 inline-block w-1/2 rounded-[6.5px] border-[1px] border-light-gray pb-2 pl-3 pr-3 pt-2 text-xs leading-6 placeholder-dark-gray">
          Loading...
        </div>
      ) : (
        <DatePicker
          selected={startDate}
          dateFormat="yyyy/MM/dd"
          className="mb-10 ml-6 mt-2 w-1/2 rounded-[6.5px] border-light-gray text-xs placeholder-dark-gray"
          onChange={(date) => setStartDate(date)}
        />
      )}

      <div className="flex justify-between">
        <span>Invitation List:</span>
        <button
          type="button"
          disabled={createOfferingInProgress || offerings.length > 0}
          onClick={handleFileUploadButton}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={3}
            stroke="currentColor"
            className={clsx(
              'h-6 w-6 text-skin-accent',
              createOfferingInProgress || offerings.length > 0
                ? 'cursor-not-allowed opacity-80'
                : 'hover:text-skin-accent-hover'
            )}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4.5v15m7.5-7.5h-15"
            />
          </svg>
        </button>
        <CSVReader
          inputId="upload-list"
          cssClass="hidden"
          onFileLoaded={handleFileLoaded}
        />
      </div>
      {/** List */}
      <div className="mb-10 ml-6 mt-2 h-[350px] overflow-y-scroll rounded-[6px] border-[1px] border-light-gray px-2">
        <table>
          <tbody>
            {invitesDataLoading ? (
              <AdminListItem email="Loading..." />
            ) : invites?.length > 0 ? (
              invites
                .map((edge) => edge.node)
                .sort((a, b) => sortStringFunc(a.email, b.email))
                .map(({ email, role }) => {
                  return <AdminListItem key={email} email={email} role={role} />
                })
            ) : (
              inviteList.length > 0 &&
              inviteList
                .sort((a, b) => sortStringFunc(a.email, b.email))
                .map(({ email, role }) => {
                  return (
                    <AdminListItem
                      key={email}
                      email={email}
                      role={role}
                      onSelectRole={handleRoleSelection}
                    />
                  )
                })
            )}
          </tbody>
        </table>
      </div>
      {/** Create offering button / status */}
      {!offeringsDataLoading && !invitesDataLoading && (
        <div className="text-center">
          {offerings && offerings.length > 0 ? (
            <span className="rounded-full bg-yellow px-8 pb-3 pt-[0.6rem] font-bold">
              Offering is currently running
            </span>
          ) : (
            <button
              type="button"
              disabled={createOfferingInProgress}
              className={clsx(
                'rounded-full bg-skin-button-accent px-8 pb-3 pt-[0.6rem] text-white transition-colors',
                createOfferingInProgress
                  ? 'cursor-not-allowed opacity-80'
                  : 'hover:bg-skin-button-accent-hover active:translate-y-[0.75px] active:scale-[0.99]'
              )}
              onClick={handleCreateNewOffering}
            >
              {createOfferingInProgress
                ? `(${completedPercentage}%) Creating in progress...`
                : 'Create your new Offering'}
            </button>
          )}
        </div>
      )}
    </>
  )
}

export default AdminNewOffering
